import { useEffect, useMemo } from 'react';
import { useConsentStore } from './consent.store';
import type { ConsentCategory } from './types';
import { INTERNAL_ROLES } from './types';

interface UseConsentReturn {
  granted: ConsentCategory[];
  grantedAt: string | null;
  grantConsent: (categories: ConsentCategory[]) => void;
  grantAll: () => void;
  hasConsent: (category: ConsentCategory) => boolean;
  revokeConsent: (categories: ConsentCategory[]) => void;
  resetConsent: () => void;
  showBanner: boolean;
}

function getUserFromStorage(): { roles: string[] } | null {
  try {
    const authStorage = localStorage.getItem('auth-storage');
    if (!authStorage) return null;

    const parsed = JSON.parse(authStorage) as {
      state?: { user?: { roles?: string[] } };
    };

    if (parsed?.state?.user?.roles) {
      return { roles: parsed.state.user.roles };
    }
    return null;
  } catch {
    return null;
  }
}

export function useConsent(): UseConsentReturn {
  const consent = useConsentStore();

  const user = useMemo(() => getUserFromStorage(), []);

  useEffect(() => {
    if (!user) return;

    const isInternal = user.roles.some((r) =>
      INTERNAL_ROLES.includes(r as (typeof INTERNAL_ROLES)[number])
    );

    if (isInternal && !consent.hasConsent('analytics')) {
      consent.grantAll();
    }
  }, [user, consent]);

  const showBanner = consent.needsConsent() && user !== null;

  return {
    granted: consent.granted,
    grantedAt: consent.grantedAt,
    grantConsent: consent.grantConsent,
    grantAll: consent.grantAll,
    hasConsent: consent.hasConsent,
    revokeConsent: consent.revokeConsent,
    resetConsent: consent.resetConsent,
    showBanner,
  };
}
